import useMediaQuery from './useMediaQuery'

export type DeviceClass = 'phone' | 'tablet' | 'desktop'

/** The width at which a touch device stops being a phone. */
const TABLET_MIN_WIDTH = 768

/**
 * Which kind of device this is — by pointer as well as width.
 *
 * The app currently holds four different opinions about "mobile":
 *
 *   useIsMobile                     < 1024px
 *   Tailwind `md:` (sidebar, header)  768px
 *   ChatPopup.tsx                     768px, hardcoded
 *   NoteAddCN.tsx                     47.9375rem, as a raw string
 *
 * An iPad lands in the middle of all of them. Portrait is ~820px and reads as
 * "mobile"; landscape is ~1180px and reads as "desktop". The visible symptom
 * today is that an iPad user cannot open an evaluation in portrait but can by
 * rotating the device, because the route guard gates on `useIsMobile`.
 *
 * Width alone cannot fix that: a laptop at 1180px and an iPad at 1180px are
 * identical by width. `(pointer: coarse)` is the only signal that separates
 * them, and it is already used for exactly this kind of decision in the chat
 * Composer, which checks it to decide whether Enter inserts a newline.
 *
 * Note this describes the DEVICE, not what the user wants. A tablet user may
 * legitimately prefer the desktop app; that is a separate, user-visible choice
 * and must not be inferred from pixels.
 */
const useDeviceClass = (): DeviceClass => {
	const isCoarse = useMediaQuery('(pointer: coarse)')
	const isNarrow = useMediaQuery(`(max-width: ${TABLET_MIN_WIDTH - 1}px)`)

	if (!isCoarse) return 'desktop'
	return isNarrow ? 'phone' : 'tablet'
}

export default useDeviceClass
